import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './datePicker.css';
import { format, isSameDay, parseISO } from 'date-fns';
import { Button } from '@mui/material';
import { AdHocClosingDay } from 'src/interfaces/interfaces';

interface SelectableCalendarProps {
  initialSelectedDates: AdHocClosingDay[]; // Updated to expect AdHocClosingDay[]
  onSubmit: (result: { added: AdHocClosingDay[]; removed: AdHocClosingDay[] }) => void;
}

const SelectableCalendar: React.FC<SelectableCalendarProps> = ({
  initialSelectedDates,
  onSubmit
}) => {
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [addedDates, setAddedDates] = useState<AdHocClosingDay[]>([]);
  const [removedDates, setRemovedDates] = useState<AdHocClosingDay[]>([]);
  const [stateChanged, setStateChanged] = useState(false);

  useEffect(() => {
    const initialDates = initialSelectedDates.map((dateObj) => parseISO(dateObj.date));
    setSelectedDates(initialDates);
  }, [initialSelectedDates]);

  const handleDateClick = (date: Date | null) => {
    if (!date) return;

    setStateChanged(true);

    const foundDate = selectedDates.find((selectedDate) =>
      isSameDay(selectedDate, date)
    );

    if (foundDate) {
      // If date was initially selected, add to removedDates
      const dateObj = initialSelectedDates.find((d) =>
        isSameDay(parseISO(d.date), date)
      );
      if (dateObj) {
        setRemovedDates([...removedDates, dateObj]);
      }
      // Remove date from selectedDates
      setSelectedDates(
        selectedDates.filter((selectedDate) => !isSameDay(selectedDate, date))
      );
      // Remove from addedDates if it was previously added
      setAddedDates(addedDates.filter((d) => !isSameDay(parseISO(d.date), date)));
    } else {
      // If the date is newly selected, add to addedDates unless it was part of the initial dates

      const newDateObj = {
        id: -1,
        date: format(date, 'yyyy-MM-dd')
      };
      setSelectedDates([...selectedDates, date]);

      // If date was initially selected, add to removedDates
      const dateObj = initialSelectedDates.find((d) =>
        isSameDay(parseISO(d.date), date)
      );
      if (!dateObj) {
        setAddedDates([...addedDates, newDateObj]);
      }

      // Remove from removedDates if it was previously removed
      setRemovedDates(removedDates.filter((d) => !isSameDay(parseISO(d.date), date)));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit({ added: addedDates, removed: removedDates });
  };

  return (
    <form onSubmit={handleSubmit}>
      <DatePicker
        inline
        selected={null}
        onSelect={handleDateClick}
        highlightDates={selectedDates}
        dayClassName={(date) =>
          selectedDates.some((selectedDate) => isSameDay(selectedDate, date))
            ? 'selected-date'
            : ''
        }
      />
      <div>
        {stateChanged && (
          <Button
            disabled={!stateChanged || (addedDates.length === 0 && removedDates.length === 0) }
            variant='contained'
            color='primary'
            type='submit'
          >
            Save Changes
          </Button>
        )}
      </div>
    </form>
  );
};

export default SelectableCalendar;
